'use client';

import { useState, useCallback } from 'react';
import { analyseDocument, DigitalScribeResult } from '@/lib/ai/digitalScribe';

interface UseDigitalScribeReturn {
  result: DigitalScribeResult | null;
  isAnalysing: boolean;
  error: string | null;
  analyseFile: (file: File) => Promise<DigitalScribeResult | null>;
  reset: () => void;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

export function useDigitalScribe(): UseDigitalScribeReturn {
  const [result, setResult] = useState<DigitalScribeResult | null>(null);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyseFile = useCallback(async (file: File): Promise<DigitalScribeResult | null> => {
    setIsAnalysing(true);
    setError(null);
    setResult(null);

    try {
      const base64DataUri = await fileToBase64(file);
      const data = await analyseDocument(base64DataUri);
      setResult(data);
      return data;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Digital Scribe analysis failed. Please try again.';
      setError(message);
      return null;
    } finally {
      setIsAnalysing(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setIsAnalysing(false);
  }, []);

  return { result, isAnalysing, error, analyseFile, reset };
}
